import type {CSSProperties} from 'react';
import {T} from '@territory/tokens';
import {radius, weight} from '../scale.js';

export type WedgeBv2KitLook = 'bv2kit-fold' | 'bv2kit-action';

const BV2_KIT_LOOKS = new Set<string>(['bv2kit-fold', 'bv2kit-action']);
export const isWedgeBv2KitLook = (look: string): look is WedgeBv2KitLook => BV2_KIT_LOOKS.has(look);

/* Released brief v2 kit buttons (prep-brief-v2-kit), off-scale. The fold toggle is the 36px "-" and "+" box at the end of a card header: an
   11px radius, a 1px border, a filled field and the secondary ink. The empty-state action is a 36px high box with 14px side padding, a 10px
   radius, a 1px border, no fill, the accent ink and 13px semibold type. The caller sets the font family. Colors read the --wedge-bv2-*
   roles, which the caller points at the brief scope vars (--bv2-line, --bv2-card2, --bv2-sec, --bv2-line2 and --bv2-accInk); outside the
   brief each falls back to a base token. Both boxes are below 44px in the shipped screen and had no transparent target there. Neither
   look draws one: the anchor would add a positioned box beside the card header action and the empty-state text, and legacy has none
   (wedgeBv2KitDrawsHit). */
const role = (name: string, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;
export const wedgeBv2KitDrawsHit = (look: string): boolean => !isWedgeBv2KitLook(look);

export function wedgeBv2KitStyle(look: WedgeBv2KitLook): CSSProperties {
  if (look === 'bv2kit-fold') {
    return {
      width: 36, height: 36, flexShrink: 0, padding: 0, borderRadius: 11, cursor: 'pointer',
      border: `1px solid ${role('line', T.separator)}`, background: role('card2', T.surfaceAlt), color: role('sec', T.labelSec),
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    };
  }
  return {
    height: 36, padding: '0 14px', borderRadius: radius.md, border: `1px solid ${role('line2', T.separator)}`, background: 'transparent',
    color: role('acc-ink', T.accent), fontSize: 13, fontWeight: weight.semibold, cursor: 'pointer',
  };
}
